import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '../store/sessionStore.js';
import { useAuthStore }    from '../store/authStore.js';
import { useHubStore }     from '../lib/hub.js';
import { getTerminals, openSession, getActiveSession, crearCaja } from '../api/pos.api.js';
import styles from './OpenSessionScreen.module.css';

const LogoutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

// Dónde se dan de alta las cajas: la app web, no el POS.
const APP_WEB = 'app.geekcommerce.digital';

export default function OpenSessionScreen() {
  const navigate       = useNavigate();
  // Por qué acabó aquí. Se pone cuando el cobro rebotó porque la caja ya se
  // había cerrado en otro lado: sin esto vería la pantalla de apertura sin
  // explicación y creería que perdió la venta.
  const aviso          = useSessionStore((s) => s.aviso);
  const openSess       = useSessionStore((s) => s.openSession);
  const closeSess      = useSessionStore((s) => s.closeSession);
  const user           = useAuthStore((s) => s.user);
  const logout         = useAuthStore((s) => s.logout);
  const hubUrl         = useHubStore((s) => s.hubUrl);
  const hubName        = useHubStore((s) => s.hubName);

  const [terminals,    setTerminals]    = useState([]);
  const [terminalId,   setTerminalId]   = useState('');
  const [openingCash,  setOpeningCash]  = useState('');
  const [loading,      setLoading]      = useState(false);
  const [loadingTerms, setLoadingTerms] = useState(true);
  const [error,        setError]        = useState(null);
  const [hubSession,   setHubSession]   = useState(null);   // sesión ya abierta en la caja central
  // Esperando a que la caja central conteste. Pasa sobre todo al abrir la app
  // en la tablet que ES la caja: el servidor embebido tarda unos segundos en
  // levantarse y esta pantalla ya está preguntando.
  const [esperandoHub, setEsperandoHub] = useState(false);
  const [intento,      setIntento]      = useState(0);      // para el botón de reintentar

  useEffect(() => {
    let cancelled = false;
    const pausa = (ms) => new Promise((r) => setTimeout(r, ms));

    // Antes se preguntaba UNA vez: si la caja embebida todavía no escuchaba,
    // la pantalla se quedaba con "No se pudo hablar con la caja central" y el
    // botón deshabilitado para siempre — en cada tablet-caja, en cada arranque.
    //
    // Solo se reintenta cuando no hubo respuesta: eso es "todavía no está".
    // Una respuesta con error (401, 500…) es un "no" de verdad y se enseña ya.
    const cargar = async () => {
      const limite = Date.now() + (hubUrl ? 25_000 : 0);
      for (let n = 0; ; n++) {
        try {
          const data = await getTerminals();
          if (cancelled) return;
          setTerminals(data);
          if (data.length === 1) setTerminalId(data[0]._id);

          // Con caja central hay UNA sesión para todo el salón. Si ya está
          // abierta, la tablet se une; no abre otra.
          if (hubUrl && data[0]) {
            const active = await getActiveSession(data[0]._id);
            if (!cancelled && active) setHubSession({ session: active, terminal: data[0] });
          }
          return;
        } catch (err) {
          if (cancelled) return;
          const sinRespuesta = !err.response;
          if (hubUrl && sinRespuesta && Date.now() < limite) {
            setEsperandoHub(true);
            await pausa(Math.min(500 * (n + 1), 3000));
            if (cancelled) return;
            continue;
          }
          setError(
            err.response?.data?.error
              ?? (hubUrl ? 'No se pudo hablar con la caja principal' : 'No se pudieron cargar los terminales'),
          );
          return;
        }
      }
    };

    setError(null);
    setLoadingTerms(true);
    cargar().finally(() => {
      if (!cancelled) { setLoadingTerms(false); setEsperandoHub(false); }
    });
    return () => { cancelled = true; };
  }, [hubUrl, intento]);

  // Ya cargó, no hubo error y el punto de venta no tiene ninguna caja: sin
  // caja no se puede abrir turno, y una lista vacía no le dice a nadie qué
  // hacer. Se explica dónde se da de alta, según quién está entrando.
  const sinCajas  = !loadingTerms && !error && terminals.length === 0;
  const esAdmin   = user?.role === 'admin';
  const buscarOtraVez = () => setIntento((n) => n + 1);

  // El dueño crea la primera caja aquí mismo, sin ir al panel web.
  const [creandoCaja, setCreandoCaja] = useState(false);
  const [errorCaja,   setErrorCaja]   = useState(null);
  const crearPrimeraCaja = async () => {
    setErrorCaja(null);
    setCreandoCaja(true);
    try {
      await crearCaja('Caja 1');
      buscarOtraVez();
    } catch (err) {
      setErrorCaja(err.response?.data?.error ?? err.response?.data?.message ?? 'No se pudo crear la caja. Revisa el internet e intenta de nuevo.');
    } finally {
      setCreandoCaja(false);
    }
  };

  const handleLogout = () => {
    closeSess();       // limpia cualquier resto de sesión de caja
    logout();          // borra token/usuario → PrivateRoute redirige a /login
    navigate('/login', { replace: true });
  };

  const joinHub = () => {
    openSess(hubSession);
    navigate('/', { replace: true });
  };

  const handleOpen = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // Verificar si ya hay sesión activa en este terminal
      const active = await getActiveSession(terminalId);
      if (active) {
        const terminal = terminals.find((t) => t._id === terminalId);
        openSess({ session: active, terminal });
        navigate('/', { replace: true });
        return;
      }

      const session  = await openSession({ terminal_id: terminalId, opening_cash: Number(openingCash) || 0 });
      const terminal = terminals.find((t) => t._id === terminalId);
      openSess({ session, terminal });
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error ?? 'Error al abrir caja');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <div className={styles.header}>
          <img src="/favicon.svg" alt="Geek Commerce" className={styles.icon} />
          <h1 className={styles.title}>{hubSession ? 'Unirse a la caja' : 'Apertura de caja'}</h1>
          <p className={styles.sub}>Hola, <strong>{user?.name}</strong></p>
        </div>

        {aviso && <p className={styles.aviso}>{aviso}</p>}

        {hubSession ? (
          <div className={styles.form}>
            <p className={styles.hint} style={{ textAlign: 'center' }}>
              <strong>{hubName ?? 'La caja principal'}</strong> ya tiene la caja abierta
              {hubSession.session.cashier_name ? ` (${hubSession.session.cashier_name})` : ''}.
              Tus ventas y comandas entrarán en ese turno.
            </p>
            {error && <p className={styles.error}>{error}</p>}
            <button className="btn btn--primary btn--full" type="button" onClick={joinHub}>
              Entrar al salón
            </button>
          </div>
        ) : (
          <form className={styles.form} onSubmit={handleOpen}>
            {hubUrl && (
              <p className={styles.hint} style={{ textAlign: 'center' }}>
                Conectado a <strong>{hubName ?? 'la caja principal'}</strong>. La caja que abras aquí será la de todo el salón.
              </p>
            )}
            {sinCajas ? (
              <div className={styles.sinCajas} role="status">
                <p className={styles.sinCajasTitulo}>Este punto de venta todavía no tiene cajas</p>
                {esAdmin ? (
                  <>
                    <p className={styles.sinCajasTexto}>
                      Crea la primera aquí mismo. Cobra con tarjeta en tu propia terminal bancaria;
                      si después quieres un lector conectado, se cambia en <strong>{APP_WEB}</strong>.
                    </p>
                    <button className="btn btn--primary btn--full" type="button" onClick={crearPrimeraCaja} disabled={creandoCaja}>
                      {creandoCaja ? <span className="spinner" /> : 'Crear la Caja 1'}
                    </button>
                    {errorCaja && <p className={styles.error}>{errorCaja}</p>}
                  </>
                ) : (
                  <p className={styles.sinCajasTexto}>
                    Pídele al administrador del negocio que dé de alta una en <strong>{APP_WEB}</strong>,
                    en Canales → punto de venta → Agregar terminal. Después toca <strong>Buscar de nuevo</strong>.
                  </p>
                )}
                <button className="btn btn--secondary btn--full" type="button" onClick={buscarOtraVez}>
                  Buscar de nuevo
                </button>
              </div>
            ) : (
            <>
            <div className={styles.field}>
              <label className={styles.label}>Terminal</label>
              {loadingTerms ? (
                <div className={styles.loading}>
                  <span className="spinner" />
                  {esperandoHub && <p className={styles.hint}>Arrancando la caja principal…</p>}
                </div>
              ) : (
                <select
                  className="input"
                  value={terminalId}
                  onChange={(e) => setTerminalId(e.target.value)}
                  required
                >
                  <option value="">Seleccionar terminal…</option>
                  {terminals.map((t) => (
                    <option key={t._id} value={t._id}>{t.name}</option>
                  ))}
                </select>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Efectivo en caja al abrir</label>
              <div className={styles.currencyInput}>
                <span className={styles.currencySymbol}>$</span>
                <input
                  className="input"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={openingCash}
                  onChange={(e) => setOpeningCash(e.target.value)}
                  style={{ paddingLeft: 32 }}
                />
              </div>
              <p className={styles.hint}>Cuenta el efectivo inicial antes de abrir</p>
            </div>
            </>
            )}

            {error && <p className={styles.error}>{error}</p>}
            {/* Si la caja central no contestó ni esperando, que se pueda volver
                a intentar sin salir de la pantalla. */}
            {error && !terminals.length && (
              <button className="btn btn--ghost btn--full" type="button" onClick={() => setIntento((n) => n + 1)}>
                Reintentar
              </button>
            )}

            {!sinCajas && (
              <button
                className="btn btn--primary btn--full"
                type="submit"
                disabled={loading || !terminalId}
              >
                {loading ? <span className="spinner" /> : 'Abrir caja'}
              </button>
            )}
          </form>
        )}

        <div className={styles.footer}>
          <button type="button" className={styles.logoutBtn} onClick={() => navigate('/hub')} disabled={loading}>
            {hubUrl ? (hubName && hubName !== 'Caja principal' ? `Caja principal: ${hubName}` : 'Conectado a la caja principal') : 'Este aparato'}
          </button>
          <button
            type="button"
            className={styles.logoutBtn}
            onClick={handleLogout}
            disabled={loading}
          >
            <LogoutIcon />
            Cerrar sesión de {user?.name?.split(' ')[0] ?? 'cajero'}
          </button>
        </div>
      </div>
    </div>
  );
}
